<template>
  <div class="fts-auth">
    <!-- trigger -->
    <fts-tippy
      :options="{
        interactive: true,
        placement: previewPlacement,
        delay: [500 , 0],
        theme: 'light-border',
      }"
      popper-prevent-overflow
      @onTrigger="setRights"
    >
      <template #trigger>
        <slot name="trigger" :show="show" :isRight="isRight">
          <font-awesome-icon
            slot="trigger"
            class="auth-toggle lock fts-text-danger"
            :icon="['fas', 'lock']"
            fixed-width
            @click="show"
            v-if="isRight"
          />
          <font-awesome-icon
            slot="trigger"
            class="auth-toggle fts-text-default"
            :icon="['fas', 'unlock']"
            fixed-width
            @click="show"
            v-else
          />
        </slot>
      </template>
      <!-- preview -->
      <div class="auth-preview" slot="content">
        <div class="auth-preview__header">
          Access <span v-if="title">for {{title}}</span>
        </div>
        <div class="auth-preview__content fts-scrollbar">
          <!-- blank -->
          <div class="auth-preview-blank" v-if="!Boolean(rights && rights.length)">
            <font-awesome-icon
              :icon="['fas', 'check-circle']"
              fixed-width
              size="lg"
              class="fts-text-success"
            />
            Access granted for everyone in the workspace.
          </div>
          <!-- everyone badge -->
          <div class="auth-preview-identity" v-if="isEveryoneAccess">
            <fts-tippy content="Allow">
              <font-awesome-icon
                slot="trigger"
                :icon="['fas', 'check-circle']"
                fixed-width
                class="auth-preview-identity__right fts-text-success"
              />
            </fts-tippy>
            <div class="fts-identity">
              <font-awesome-icon
                :icon="['fas', 'user-friends']"
                fixed-width
                class="identity__icon"
              />
              <div class="identity__name">Everyone is allow access</div>
              <div class="identity__badge">Workspace</div>
            </div>
          </div>
          <!-- rights -->
          <div
            v-for="(auth, index) in rights"
            :key="`auth-preview-${auth.entityId}-${index}`"
            class="auth-preview-identity"
          >
            <!-- right -->
            <!-- right:allow -->
            <fts-tippy content="Allow" v-if="auth.r">
              <font-awesome-icon
                slot="trigger"
                :icon="['fas', 'check-circle']"
                fixed-width
                class="auth-preview-identity__right fts-text-success"  
              />
            </fts-tippy>
            <!-- right:deny -->
            <fts-tippy content="Deny" v-else>
              <font-awesome-icon
                slot="trigger"
                :icon="['fas', 'minus-circle']"
                fixed-width
                class="auth-preview-identity__right fts-text-danger"
              />
            </fts-tippy>
            <!-- member -->
            <fts-profile
              :user-name="auth.entityName"
              :profile-photo-size="18"
              profile-name-class="fts-identity identity-profile"
              v-if="auth.entityType === 0"
            >
              <template #profileName="{userProfileUsername}">
                <div class="identity__name">{{auth.entity}}</div>
                <div class="identity__tag">{{userProfileUsername}}</div>
                <div class="identity__badge">User</div>
              </template>
            </fts-profile>
            <!-- user profile -->
            <div class="fts-identity" v-else-if="auth.entityType === 5">
              <fts-moji
                :emoji="getModuleEmoji(auth.entityId)"
                :size="18"
                class="identity__emoji"
                v-if="getModuleEmoji(auth.entityId)"
              />
              <div class="identity__name">{{auth.entity}}</div>
              <div class="identity__badge">Profile</div>
            </div>
            <!-- system -->
            <div class="fts-identity" v-else>
              <font-awesome-icon
                :icon="['fas', 'user-friends']"
                fixed-width
                class="identity__icon"
              />
              <div class="identity__name">{{auth.entity}}</div>
              <div class="identity__badge">Workspace</div>
            </div>
          </div>
        </div>
      </div>
    </fts-tippy>
    <!-- modal -->
    <fts-design-modal
      width="1200px"
      :backdrop="false"
      desc="access configuration"
      appendContentClass="auth-modal__body"
      class="fts-auth-modal"
      ref="ftsDesignModal"
      @closed="reset"
    >
      <!-- title -->
      <template #title>
        <div class="design-modal-header-std__title">
          <font-awesome-icon
            class="design-modal-header-icon fts-text-danger"
            :icon="['fas', 'lock']"
          />
          <div class="design-modal-header-name">
            Access <span v-if="title">for {{title}}</span>
          </div>
        </div>
      </template>
      <!-- body -->
      <template #body>
        <!-- spinner -->
        <div class="design-modal-spinner" v-if="loading">
          <fts-spinner show />
        </div>
        <template v-else>
          <!-- auth:select -->
          <fts-select
            :form-data="formData"
            :item="item"
            :options="options"
            :reduce="x => x"
            :get-option-key="getOptionKey"
            :clear-search-on-blur="() => true"
            :clear-search-on-select="false"
            :close-on-select="false"
            comfort-design
            class="auth-select"
            @input="selectRole"
            ref="authSelect"
          >
            <template #option="{option}">
              <!-- member -->
              <fts-profile
                :user-name="option.entityName"
                :profile-photo-size="20"
                profile-name-class="fts-identity identity-profile"
                v-if="option.type === 0"
              >
                <template #profileName="{userProfileUsername}">
                  <div class="identity__name">{{option.name}}</div>
                  <div class="identity__tag">{{userProfileUsername}}</div>
                  <div class="identity__badge">User</div>
                </template>
              </fts-profile>
              <!-- user profile -->
              <div class="fts-identity" v-else-if="option.type === 5">
                <fts-moji
                  :emoji="getModuleEmoji(option.id)"
                  :size="20"
                  class="identity__emoji"
                  v-if="getModuleEmoji(option.id)"
                />
                <div class="identity__name">{{option.name}}</div>
                <div class="identity__badge">Profile</div>
              </div>
              <!-- system -->
              <div class="fts-identity" v-else>
                <font-awesome-icon
                  :icon="['fas', 'user-friends']"
                  fixed-width
                  class="identity__icon"
                />
                <div class="identity__name">{{option.name}}</div>
                <div class="identity__badge">Workspace</div>
              </div>
            </template>
          </fts-select>
          <!-- auth:list -->
          <div class="auth-entity-container">
            <div class="auth-entity-title" v-if="rights.length">
              Only allow access for
            </div>
            <!-- blank -->
            <div class="auth-entity-blank" v-else>
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                fixed-width
                size="lg"
                class="auth-entity-blank__icon fts-text-success"
              />
              Access granted for everyone in the workspace.
            </div>
            <!-- everyone badge -->
            <div class="fts-identity auth-entity-everyone" v-if="isEveryoneAccess">
              <font-awesome-icon
                :icon="['fas', 'check-circle']"
                class="auth-entity-everyone__icon fts-text-success"
                size="lg"
                fixed-width
              />
              <font-awesome-icon
                :icon="['fas', 'user-friends']"
                fixed-width
                class="identity__icon"
              />
              <div class="identity__name">Everyone is allow access</div>
              <div class="identity__badge">Workspace</div>
            </div>
            <!-- entity -->
            <div class="auth-entity" v-for="auth in rights" :key="auth.key">
              <!-- checkbox -->
              <checkbox
                color="var(--fts-primary)"
                :size="18"
                v-model="auth.r"
                class="auth-entity__checkbox"
              />
              <!-- member -->
              <fts-profile
                :user-name="auth.entityName"
                :profile-photo-size="20"
                profile-name-class="fts-identity identity-profile"
                class="auth-entity__identity"
                :key="auth.entityId"
                v-if="auth.entityType === 0"
              >
                <template #profileName="{userProfileUsername, userProfileName}">
                  <div class="identity__name">{{userProfileName || auth.entity}}</div>
                  <div class="identity__tag">{{userProfileUsername}}</div>
                  <div class="identity__badge">User</div>
                </template>
              </fts-profile>
              <!-- user profile -->
              <div class="auth-entity__identity fts-identity" v-else-if="auth.entityType === 5">
                <fts-moji
                  :emoji="getModuleEmoji(auth.entityId)"
                  :size="20"
                  class="identity__emoji"
                  v-if="getModuleEmoji(auth.entityId)"
                />
                <div class="identity__name">{{auth.entity}}</div>
                <div class="identity__badge">Profile</div>
              </div>
              <!-- system -->
              <div class="auth-entity__identity fts-identity" v-else>
                <font-awesome-icon
                  :icon="['fas', 'user-friends']"
                  fixed-width
                  class="identity__icon"
                />
                <div class="identity__name">{{auth.entity}}</div>
                <div class="identity__badge">Workspace</div>
              </div>
              <!-- action -->
              <fts-btn
                icon
                text
                class="auth-entity__action"
                @click="removeRole(auth)"
              >
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </fts-btn>
            </div>
          </div>
        </template>
      </template>
      <!-- footer:right -->
      <template #footerRight>
        <fts-btn
          text
          @click="close"
        >
          Cancel
        </fts-btn>

        <fts-btn
          outline
          color="primary"
          :disabled="loading"
          @click="done"
        >
          <font-awesome-icon :icon="['fas', 'check']" /> Ok
        </fts-btn>
      </template>
    </fts-design-modal>
  </div>
</template>

<script>
import { store as canvasDesignStore } from "../store/canvasDesignStore";

import FTSDesignModal from "./FTSDesignModal";
import FTSSelect from "../elements/FTSSelect";
import FTSBtn from "../elements/FTSBtn";
import FTSProfile from "../elements/FTSProfile";
import FTSMoji from "../elements/FTSMoji";
import FTSSpinner from "../elements/FTSSpinner";
import FTSTippy from "../elements/FTSTippy";

import Checkbox from "vue-material-checkbox";

export default {
  props: {
    type: String,
    unitId: String,
    title: String,
    /* tippy */
    previewPlacement: {
      type: String,
      default: 'left-start',
    },
  },
  components: {
    "fts-design-modal": FTSDesignModal,
    "fts-select": FTSSelect,
    "fts-btn": FTSBtn,
    "fts-profile": FTSProfile,
    "fts-moji": FTSMoji,
    "fts-spinner": FTSSpinner,
    "fts-tippy": FTSTippy,
    "checkbox": Checkbox,
  },
  data: () => ({
    formData: {},
    item: {
      fieldName: "role",
      options: [],
    },
    rights: [],
    loading: false,
  }),
  methods: {
    async show() {
      this.loading = true;
      this.$refs.ftsDesignModal.show();
      this.setRights();

      try {
        const res = await canvasDesignStore.dispatch('retrieveAuthorizationEntities', {
          vectorId: canvasDesignStore.state.vectorId
        });

        this.item.options = res.data.map(x =>
          (x.name === 'Everyone' || x.name === 'Guest') &&
          !x.id ? Object.assign(x, {id: x.name.toLowerCase()}) : x
        );
      } catch(err) {
        console.error(err);
      }

      this.loading = false;
    },
    done() {
      let authorizations = [];
      if (this.type === "vector" || this.type === "import")
        authorizations = [
          ...this.authorizations.filter(x =>
            x.authorizationType != this.type
          )
        ];
      else
        authorizations = [
          ...this.authorizations.filter(x =>
            x.authorizationUnitId != this.unitId
          )
        ];

      authorizations = authorizations.concat(this.rights);
      canvasDesignStore.dispatch('updateAuthorization', authorizations);
      this.close();
    },
    close() {
      this.$refs.ftsDesignModal.close();
    },
    reset() {
      Object.assign(this.$data, this.$options.data.apply(this));
    },
    setRights() {
      this.rights = _.cloneDeep(this.getRights);
      this.rights.filter(x => x.entityType === 5)
        .forEach(x => {
          const res =  [...canvasDesignStore.getters.workspaceModules].find(y => y.id == x.entityId);
          if (res && res.name !== x.entity)
            x.entity = res.name;
        });
    },
    getOptionKey(option) {
      if (typeof option === 'object' && option.id && option.name) {
        const index = this.item.options.indexOf(option);
        return `${index}-${option.id}`;
      } else {
        try {
          return JSON.stringify(option);
        } catch(err) {
          console.warn("Auth:GetOptionkey", err);
        }
      }
    },
    getModuleEmoji(moduleId) {
      try {
        const findModule = canvasDesignStore.getters.workspaceModules.find(x =>
          x.id == moduleId
        );
        return findModule && findModule.emoji;
      } catch(err) {
        console.warn("Auth:GetModuleEmoji", err);
      }
    },
    selectRole(val) {
      if (val) {
        /* override vSelect behavior */
        try {        
          const dropdownMenu = document.getElementsByClassName('vs__dropdown-menu')[0];
          const selectedEl = dropdownMenu.children[this.$refs.authSelect.$refs.ftsSelect.typeAheadPointer];
          const scrollHeight = dropdownMenu.scrollTop;
          const preElSib = selectedEl && selectedEl.previousElementSibling;
          const nxtElSib = selectedEl && selectedEl.nextElementSibling;

          function setPosition(vm, el) {
            let index = Array.prototype.indexOf.call(document.getElementsByClassName('vs__dropdown-menu')[0].children, el);
            vm.$refs.authSelect.$refs.ftsSelect.typeAheadPointer = index;
            vm.$nextTick(() => {
              document.getElementsByClassName('vs__dropdown-menu')[0].scrollTo(0, scrollHeight);
            });
          } 

          this.$nextTick(() => {
            if (nxtElSib)
              setPosition(this, nxtElSib);
            else if (preElSib) 
              setPosition(this, preElSib);
          });
        } catch(err) {
          console.warn("[DOMException]", err);
        }
        /* set auth */
        const res = {...val};
        this.formData.role = null;

        /* initial set everyone */
        if (val.id !== 'everyone' && !this.rights.length) {
          const everyoneEntity = this.item.options.find(x => x.id === 'everyone');
          if (everyoneEntity) 
            this.rights.push({
              entity: everyoneEntity.name,
              entityName: everyoneEntity.entityName,
              entityId: everyoneEntity.id,
              entityType: everyoneEntity.type,
              authorizationUnitId: this.unitId,
              authorizationType: this.type,
            });
        }

        if (
          !this.rights.some(x =>
            x.entity == res.name &&
            x.entityId == res.id &&
            x.entityType == res.type
          )
        ) {
          this.rights.push({
            entity: res.name,
            entityName: res.entityName,
            entityId: res.id,
            entityType: res.type,
            authorizationUnitId: this.unitId,
            authorizationType: this.type,
            r: true,
          });
        }
      }
    },
    removeRole(auth) {
      this.rights.splice(this.rights.indexOf(auth), 1);
    },
  },
  computed: {
    options() {
      return this.item.options.filter(x =>
        !this.rights.some(y =>
          y.entityId == x.id && y.entityType == x.type
        )
      );
    },
    getRights() {
      try {
        if (this.type === 'vector' || this.type === 'import')
          return this.authorizations.filter(x => x.authorizationType == this.type);
        else
          return this.authorizations.filter(x => x.authorizationUnitId == this.unitId);
      } catch(err) {
        console.error("Auth:GetRight", err);
      }
    },
    isRight() {
      return Boolean(this.getRights && this.getRights.length);
    },
    authorizations() {
      try {
        return canvasDesignStore.state.canvas.authorizations || [];
      } catch(err) {
        console.warn("Auth:Authorizations", err);
        return [];
      }
    },
    isEveryoneAccess() {
      return this.rights && this.rights.length && !this.rights.some(x => x.entityType == 4);
    },
  },
}
</script>

<style lang="scss">
@import "./src/assets/scss/vars.scss";

/* fts-auth */
.fts-auth {
  text-align: initial;
}
/* auth toggle */
.auth-toggle {
  font-size: .75rem;
}
/* auth-modal */
.fts-auth-modal {
  .auth-modal__body {
    display: flex;
    align-items: flex-start;
  }
}
/* auth select */
.auth-select {
  @include position($position: sticky, $top: 0);
  $marginRight: 2rem;
  $width: calc(50% - $marginRight);
  flex: 1 1 $width;
  min-width: 400px;
  max-width: $width;
  margin-right: $marginRight;
}
/* auth entity */
.auth-entity-container {
  flex: 1 1 50%;
  min-width: 450px;
  .auth-entity-blank {
    padding: .575rem;
    text-align: center;
    line-height: 1.5;
    .auth-entity-blank__icon {
      margin-right: .375rem;
    }
  }
  .auth-entity-title {
    margin-bottom: .375rem;
    padding: .575rem 0;
    font-size: .875rem;
    color: var(--fts-form-label);
    line-height: 1.5;
  }
  .auth-entity-everyone {
    margin-bottom: .75rem;
    font-size: .875rem;
    .auth-entity-everyone__icon {
      margin-left: .575rem;
      margin-right: 1rem;
    }
  }
}
.auth-entity {
  display: flex;
  align-items: center;
  padding-top: .175rem;
  padding-bottom: .175rem;
  .auth-entity__checkbox {
    width: auto;
    margin: 0 1.175rem 0 .575rem;
    .m-chckbox--label {
      padding: 0;
    }
  }
  .auth-entity__identity {
    flex: 1;
    font-size: .875rem;
    .identity__icon {
      font-size: 1rem;
    }
  }
  .auth-entity__action {
    margin-left: .75rem;
  }
}
/* auth preview */
.auth-preview {
  max-height: 300px;
  width: 348px;
  display: flex;
  flex-direction: column;
  margin: -0.375rem -0.75rem;
  .auth-preview__header {
    @include text-overflow-ellipsis;
    flex-shrink: 0;
    padding: .75rem;
    background: var(--fts-gray-2);
    font-size: .875rem;
    font-weight: 700;
    z-index: 1;
  }
  .auth-preview__content {
    padding-top: .375rem;
    padding-bottom: .375rem;
    .auth-preview-blank {
      padding: .75rem;
      text-align: center;
      color: var(--fts-gray-5);
      line-height: 1.5;
    }
    .auth-everyone-badge {
      margin-left: .375rem;
      margin-right: .375rem;
      font-size: .75rem;
    }
    .auth-preview-identity {
      display: flex;
      align-items: center;
      padding: .375rem .75rem;
      .identity__icon {
        font-size: .875rem;;
      }
      .auth-preview-identity__right {
        margin-right: .75rem;
        font-size: .875rem;
      }
    }
  }
}
</style>